import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ChoiceAnswer from "../core/ChoiceAnswer";
import Sentences from "../core/Sentences";
import { EDGE } from "../core/edgeTokens";
import SequenceChart, { describeSequence } from "./SequenceChart";
import useSequencePlayer from "./useSequencePlayer";
import { contextCount, lastShown, sourceLine, swingName } from "./sequenceModel";

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A]";

// One screen of a motion Read (Momentum, Structure) for one example (client
// layout references):
//   desktop (lg+): the chart card on the left, the task panel on the right;
//   phone: the chart on top, kept in view (sticky) while the learner reads and
//   advances evidence, with a compact task panel below it.
// The task panel holds the question, answers and Submit before Submit, and the
// result panel, one evidence item at a time (Previous / n of N / Next), the
// expandable explanation and Continue after it. Teach shows its caption, and
// after playback the same panel with the read named.
// Titles never name the answer or the example id (client).
//
//   read      the Read's settings and copy (see momentum/config.js)
//   label     progress line, e.g. "Call it · 1 of 2"
//   ctaLabel  (correct) => primary button label after the reveal
//   onDone    (correct) => void; Teach passes null
export default function SequenceStep({ read, example: ex, label, ctaLabel, onDone }) {
  const { copy } = read;
  const navigate = useNavigate();
  const teach = ex.stage === "teach";
  const context = contextCount(ex);
  const stopAt = lastShown(ex);
  // Nothing after the freeze is ever drawn (the data omits it anyway).
  const candles = ex.candles.slice(0, stopAt);
  const player = useSequencePlayer({ context, stopAt, intervalMs: read.revealMs });

  const [choice, setChoice] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [spot, setSpot] = useState(0);
  const [explainOpen, setExplainOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  const revealed = teach ? player.finished : submitted;
  const correct = submitted ? choice === ex.answer : null;
  const evidence = ex.evidence || [];
  // During a replay the spotlight waits for the candles it points at.
  const shownEvidence = revealed && !player.playing ? evidence[spot] : null;
  const activeEvidence =
    shownEvidence?.type === "swingBoundary" ? { ...shownEvidence, name: swingName(ex, shownEvidence.swing) } : shownEvidence;
  const options = read.options(ex);
  const readLine = read.readLine(ex);
  const labelOf = (id) => options.find((a) => a.id === id).label;

  const submit = () => {
    if (!choice || player.playing) return;
    setSubmitted(true);
    setSpot(0);
    setAnnouncement(`${choice === ex.answer ? copy.correct : copy.incorrect}. ${readLine}`);
  };

  const goTo = (i) => {
    setSpot(i);
    setAnnouncement(`Evidence ${i + 1} of ${evidence.length}. ${evidence[i].text}`);
  };

  return (
    <div className="mx-auto max-w-[560px] lg:max-w-[1120px]">
      {/* Back / exit (client): the header has a back arrow on phones; the
          desktop header does not, so it is here. */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className={`hidden lg:inline-flex items-center gap-1 mb-3 text-sm font-semibold text-linkTeal ${FOCUS}`}
      >
        <span aria-hidden="true">‹</span> Back
      </button>
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="font-montserrat font-bold text-title-subpage text-pageTitle">{read.title}</h1>
        <p className="shrink-0 text-xs font-semibold uppercase tracking-wider text-secondary">{label}</p>
      </div>

      <div className="mt-2 lg:mt-4 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(320px,380px)] lg:gap-8 lg:items-start">
        {/* Chart. On a phone it stays in view under the fixed header (56px). */}
        <section className="sticky top-14 z-10 -mx-4 px-4 pb-2 bg-white lg:static lg:mx-0 lg:rounded-2xl lg:border lg:border-gray-200 lg:p-5">
          <SequenceChart
            candles={candles}
            shown={player.shown}
            context={context}
            dividerAfter={read.dividerAfter}
            numbers={read.numbers}
            swings={ex.swings}
            showPrices={revealed && !!ex.source}
            evidence={activeEvidence}
            description={describeSequence(candles, player.shown, context)}
          />
          <div className="mt-1 flex items-start justify-between gap-3">
            {/* Historical information once, directly below the chart, after
                the reveal only (client). */}
            <p className="text-xs md:text-sm text-secondary">
              {revealed && ex.source ? `${copy.historical} · ${sourceLine(ex.source)}` : ""}
            </p>
            <button
              type="button"
              onClick={player.replay}
              disabled={player.playing}
              className={`shrink-0 inline-flex items-center gap-1.5 min-h-[32px] px-1 rounded-lg text-sm font-semibold ${FOCUS} ${
                player.playing ? "text-tertiary cursor-not-allowed" : "text-linkTeal"
              }`}
            >
              <ReplayIcon />
              Replay
            </button>
          </div>
        </section>

        {/* Task panel */}
        <section className="mt-2 lg:mt-0">
          {teach && (
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
              <Sentences text={ex.caption} className="text-sm md:text-base text-bodyInk" />
            </div>
          )}

          {!teach && !submitted && (
            <div>
              {/* Below lg the paragraph gap is taken out of the space above the
                  prompt, so the answers and Submit stay where they were (client). */}
              <div className="text-sm md:text-base text-bodyInk max-lg:-mt-[0.75em]">
                <Paragraphs items={read.prompt(ex)} />
              </div>
              <div className="mt-3">
                <ChoiceAnswer label={read.title} options={options} value={choice} onChange={setChoice} disabled={!player.finished} />
              </div>
              <button
                type="button"
                onClick={submit}
                disabled={!choice || player.playing}
                className={`mt-3 w-full min-h-[48px] rounded-lg font-semibold ${FOCUS} ${
                  choice && !player.playing ? "bg-primaryColor text-white" : "bg-gray-100 text-tertiary cursor-not-allowed"
                }`}
              >
                Submit
              </button>
            </div>
          )}

          {revealed && (
            <div className={`edge-fade-in ${teach ? "mt-3" : ""}`}>
              {/* Result panel, in place of the answer controls (client). */}
              <div className="rounded-xl bg-[#F3F4F6] px-4 py-3">
                {teach ? (
                  <p className="font-montserrat font-bold text-lg text-pageTitle">{read.heading(ex)}</p>
                ) : (
                  <>
                    <p className="flex items-center gap-2 font-montserrat font-bold text-xl text-pageTitle">
                      {correct ? <CheckIcon /> : <CrossIcon />}
                      {correct ? copy.correct : copy.incorrect}
                    </p>
                    <p className="mt-1 font-semibold text-base text-pageTitle">{readLine}</p>
                    {!correct && (
                      <p className="mt-1.5 text-sm text-secondary">
                        {copy.yourAnswer}: {labelOf(choice)}
                      </p>
                    )}
                  </>
                )}
              </div>

              {evidence.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-secondary">Evidence</p>
                  <p className="mt-1 min-h-[3rem] text-sm md:text-base text-bodyInk">{evidence[spot].text}</p>
                  <div className="mt-1 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => goTo(spot - 1)}
                      disabled={spot === 0}
                      className={`min-h-[44px] pr-3 text-sm font-semibold ${FOCUS} ${spot === 0 ? "text-tertiary" : "text-linkTeal"}`}
                    >
                      ‹ Previous
                    </button>
                    <span className="text-sm text-secondary">
                      {spot + 1} of {evidence.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => goTo(spot + 1)}
                      disabled={spot === evidence.length - 1}
                      className={`min-h-[44px] pl-3 text-sm font-semibold ${FOCUS} ${
                        spot === evidence.length - 1 ? "text-tertiary" : "text-linkTeal"
                      }`}
                    >
                      Next ›
                    </button>
                  </div>
                </div>
              )}

              {/* Phone: Explanation and Continue share a row to keep the
                  panel compact. Desktop: Explanation, its text, then a full
                  width Continue. */}
              <div className="mt-1 border-t border-gray-200 pt-2">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setExplainOpen((o) => !o)}
                    aria-expanded={explainOpen}
                    className={`flex-1 flex items-center justify-between min-h-[44px] text-sm font-semibold text-bodyInk ${FOCUS}`}
                  >
                    Explanation
                    <span aria-hidden="true" className={`transition-transform ${explainOpen ? "rotate-180" : ""}`}>
                      ⌄
                    </span>
                  </button>
                  <ContinueButton className="w-1/2 lg:hidden" onClick={() => onDone(teach ? null : correct)}>
                    {teach ? "Continue" : ctaLabel(correct)}
                  </ContinueButton>
                </div>
                {explainOpen && (
                  <div className="mt-1 text-sm text-bodyInk edge-fade-in">
                    <Paragraphs items={ex.explanation} />
                  </div>
                )}
                <ContinueButton className="hidden lg:block w-full mt-4" onClick={() => onDone(teach ? null : correct)}>
                  {teach ? "Continue" : ctaLabel(correct)}
                </ContinueButton>
              </div>
            </div>
          )}
        </section>
      </div>

      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}

// Paragraphs that wrap naturally, 0.75em apart (client). Lesson data gives
// them as a list, or as one string with blank lines between them.
function Paragraphs({ items }) {
  const list = Array.isArray(items) ? items : items.split(/\n\s*\n/);
  return list.map((text, i) => (
    <p key={i} className={i ? "mt-[0.75em]" : ""}>
      {text}
    </p>
  ));
}

function ContinueButton({ className, onClick, children }) {
  return (
    <button type="button" onClick={onClick} className={`min-h-[48px] rounded-lg bg-primaryColor text-white font-semibold ${FOCUS} ${className}`}>
      {children}
    </button>
  );
}

function CheckIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 8.3l3.4 3.4L13 4.8" fill="none" stroke={EDGE.teal} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Client: "Not quite" with a red X.
function CrossIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke={EDGE.red} strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function ReplayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4 12a8 8 0 1 0 2.4-5.7M4 4v4.5h4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
